import { transient, autoinject } from "aurelia-dependency-injection";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { TableMetaData } from "../../../../models/lookups/table-metadata";
import { DataType, DataGridColumn, ItemSource, SourceType } from "../../../../models/queries/queries.models";
import { NotifyPropertyChangedBase, Reflection } from "../../../../utilities/reflection";
import * as Enumerable from "linq";
import { computedFrom } from "aurelia-framework";
import { BindingEngine } from "aurelia-binding";
import { I18N } from 'aurelia-i18n';
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { PersistLookupsValuesRequest, PersistLookupRequest } from "../../../../models/lookups/lookup.models";
import { NotificationType, NotificationClientService } from "../../../../services/basics/notification-client.service";

@autoinject
@transient()
export class ManageLookups extends NotifyPropertyChangedBase {

    tableMetaDataItems: TableMetaData[] = [];
    currentTableMetaData: TableMetaData = null;
    tables: ItemSource;
    private _search: string;
    private _isSequenceSorting: boolean = true;
    private unitOfWorkObserver: UnitOfWorkObserver;

    private get isSequenceSorting(): boolean {
        return this._isSequenceSorting;
    }

    private set isSequenceSorting(value: boolean) {
        this._isSequenceSorting = value;
        this.refreshTables();
    }

    @computedFrom(Reflection.getPropertyPath<ManageLookups>(x => x.tableMetaDataItems))
    private get maxSequenceNumber(): number {
        return this.tableMetaDataItems
            ? Enumerable.from(this.tableMetaDataItems.map(meta => meta.lookup ? meta.lookup.sequenceNumber : 0)).max()
            : 0;
    }

    @computedFrom(Reflection.getPropertyPath<ManageLookups>(x => x.isSequenceSorting), Reflection.getPropertyPath<ManageLookups>(x => x.currentTableMetaData), Reflection.getPropertyPath<ManageLookups>(x => x.currentTableMetaData.lookup.sequenceNumber))
    get isSavable(): boolean {
        return this.isSequenceSorting && this.unitOfWorkObserver && this.unitOfWorkObserver.hasChanges();
    }

    get columns(): DataGridColumn[] {
        return [{
            name: "tableDisplayName",
            isSortable: !this.isSequenceSorting,
            sortOrder: 0,
            isAscending: true,
            columnOrder: 0,
            dataType: DataType.Default,
            title: this.i18N.tr('lookup-name-text'),
        }];
    }

    @computedFrom(Reflection.getPropertyPath<ManageLookups>(x => x._search))
    get search(): string {
        return this._search;
    }

    set search(x: string) {
        this._search = x;
        this.refreshTables();
    }

    constructor(private lookupClientService: LookupClientService, private notificationClient: NotificationClientService, private engine: BindingEngine, private i18N: I18N) {
        super();
    }

    activate(): void {
        this.loadLookups();
    }

    deactivate(): void {
        this.unitOfWorkObserver.dispose();
    }

    loadLookups() {
        this.lookupClientService.getTableMetadata().then((response) => {
            this.tableMetaDataItems.splice(0, this.tableMetaDataItems.length);
            for (const item of response.tableMetadatas) {
                this.tableMetaDataItems.push(item);
            }
            this.refreshTables();
            if (this.unitOfWorkObserver) {
                this.unitOfWorkObserver.dispose();
            }
            this.unitOfWorkObserver = UnitOfWorkObserver.getTrackingInstance(this.engine, { lookupValues: this.tableMetaDataItems });
        }).catch((e) => {
            console.error(e);
        });
    }

    refreshTables() {
        this.tables = {
            source: Enumerable.from(this.tableMetaDataItems)
                .where(x=> x.tableName.indexOf(".") == -1) // remove tables from other schemas
                .where(x => !this.search || x.tableDisplayName.toLowerCase().indexOf(this.search.toLowerCase()) != -1).orderBy(x => x.tableDisplayName).toArray(),
            sourceType: SourceType.Collection,
            columns: this.columns
        };
    }

    selectLookupValues(event): void {
        this.currentTableMetaData = event.detail.item;
    }

    moveUp(metadata: TableMetaData): void {
        if (!(metadata && !!metadata.lookup && metadata.lookup.sequenceNumber > 1)) {
            return;
        }

        this.tableMetaDataItems.filter(meta => meta.lookup.sequenceNumber == metadata.lookup.sequenceNumber - 1).forEach(meta => {
            meta.lookup.sequenceNumber++;
        })

        metadata.lookup.sequenceNumber--;
        this.refreshTables();
    }

    moveDown(metadata: TableMetaData): void {
        if (!(metadata && !!metadata.lookup && metadata.lookup.sequenceNumber < this.maxSequenceNumber)) {
            return;
        }

        this.tableMetaDataItems.filter(meta => meta.lookup.sequenceNumber == metadata.lookup.sequenceNumber + 1).forEach(meta => {
            meta.lookup.sequenceNumber--;
        })

        metadata.lookup.sequenceNumber++;
        this.refreshTables();
    }

    save(): void {
        if (!(this.unitOfWorkObserver && this.unitOfWorkObserver.hasChanges())) {
            return;
        }

        const ids: number[] = [];

        for (const property in this.unitOfWorkObserver.getChanges().lookupValues) {
            const id: number = Number(property);
            const change: TableMetaData = this.tableMetaDataItems.filter(item => item.id == id)[0];

            if (isNaN(id) || !change) {
                continue;
            }

            ids.push(change.id);
        }

        const request: PersistLookupsValuesRequest = {
            tableMetadataIds: ids,
            persistLookupsRequest: <PersistLookupRequest>{ changes: this.unitOfWorkObserver.getChanges() }
        };

        this.lookupClientService.updateLookupsOrdering(request).then(() => {
            this.notificationClient.show(this.i18N.tr('successfully-saved-text') + "!", this.i18N.tr('lookup-has-been-successfully-saved-text'), NotificationType.Success);
        }).catch(() => {
            this.notificationClient.show(this.i18N.tr('error-text') + "!", this.i18N.tr('unknown-error-text') + ".", NotificationType.Error);
        });
    }

    goBack() {
        this.currentTableMetaData = null;
    }
}
